import { createContext, useContext, useEffect, useState, useCallback } from "react";
import client, { apiError } from "@/api/client";

const AuthCtx = createContext(null);
const TOKEN_KEY = "token";
const PERSIST_KEY = "token_persist";  // "1" → localStorage, "0" → sessionStorage

// Reads the token from the storage that was selected at login time.
const readToken = () => {
  const persist = localStorage.getItem(PERSIST_KEY);
  if (persist === "0") return sessionStorage.getItem(TOKEN_KEY);
  return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
};

const writeToken = (token, rememberMe) => {
  // Always wipe both buckets first so there's never a stale token hanging around
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  if (rememberMe) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(PERSIST_KEY, "1");
  } else {
    sessionStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(PERSIST_KEY, "0");
  }
};

const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(PERSIST_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
};

export { readToken };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=none, obj=logged in
  const [ready, setReady] = useState(false);

  const bootstrap = useCallback(async () => {
    const t = readToken();
    if (!t) { setUser(false); setReady(true); return; }
    try {
      const { data } = await client.get("/auth/me");
      setUser(data);
    } catch {
      clearToken();
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = async (mobile, password, rememberMe = true) => {
    const { data } = await client.post("/auth/login", { mobile, password, remember_me: rememberMe });
    writeToken(data.access_token, rememberMe);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    clearToken();
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, ready, login, logout, apiError }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
